/**
 * The war menu — the panel that asks whether you fight the battle that has landed.
 *
 * > *"the war menu should also appear in the city once the troops reach the
 * > destination, clean it up"*
 *
 * It used to be `WorldMap.battlePrompt`: markup inside the realm screen's own
 * `innerHTML`, drawn only while that screen was up. Which meant the one moment
 * the panel exists for — a march you ordered ten minutes ago finally arriving —
 * was invisible if you had gone back to your capital in the meantime, and the
 * game simply stopped. The realm freezes on `campaign.pending`; the town does
 * not; so the player stood in a working city watching a realm that had quietly
 * halted on a question nothing on screen was asking.
 *
 * So it is a screen of its own now, driven from the frame loop, and it is the
 * *same panel in the same place* on both maps — which is the whole of what "one
 * set of furniture" means here. It is the sibling of `realmevent.ts` and holds
 * to that file's rules, bar one:
 *
 * - There is no dismiss and Escape does not close it. The realm is frozen on
 *   the question and the only thing that unfreezes it is an answer.
 * - Everything it sends back is one command or one door, and it never touches
 *   the campaign state itself.
 * - **Where it differs from the chronicle:** a chronicle question renders as a
 *   banner in the city, because the masons do not down tools over an envoy and
 *   a player can walk to the map at their leisure. A battle is not that. Both
 *   answers to it take effect somewhere the player is not standing — one of them
 *   swaps the world out from under them — so it asks in the same modal terms on
 *   both screens rather than nagging on one and blocking on the other.
 */

import { cmd, Cmd, type Command } from '../sim/commands.ts';
import type { World } from '../sim/world.ts';
import { nationName, provinceName } from '../sim/campaign/state.ts';
import { isHomeAssault } from '../sim/campaign/conquest.ts';
import type { PendingBattle } from '../sim/campaign/types.ts';

export interface WarCallCallbacks {
  /** The answer, as a command for the realm's queue wherever the player stands. */
  onCommand(c: Command): void;
  /** Take the field: stand the battle up and go and fight it by hand. */
  onFight(): void;
}

export class WarCall {
  private modal: HTMLElement;
  /**
   * Identity of the battle currently drawn, '' when none.
   *
   * The same idempotence key `realmevent.ts` uses and for the same reason: this
   * is called sixty times a second and the panel is rebuilt only when the
   * question changes. It carries the seed because two consecutive assaults on
   * the same province by the same crown are two different battles, and a key
   * that could not tell them apart would leave the first one's numbers up.
   */
  private shownKey = '';

  constructor(private cb: WarCallCallbacks) {
    injectStyle();
    const root = document.getElementById('ui-root') as HTMLElement;
    this.modal = document.createElement('div');
    this.modal.id = 'warcall';
    root.appendChild(this.modal);
  }

  /** True while the panel is up, for anything that wants to know it is blocked. */
  get isOpen(): boolean {
    return this.shownKey !== '';
  }

  /**
   * Called every frame with the realm world, or null.
   *
   * Null is not only "outside a campaign": the caller also passes null while the
   * player is *in* the fight, because `pending` is not cleared until the result
   * lands and a modal put back up over the battlefield would be a panel asking
   * whether to fight the battle being fought behind it.
   */
  update(realm: World | null): void {
    const c = realm?.campaign;
    const p = c?.pending;
    // `playerFights` is the sim's own answer to "is this one of mine, and may I
    // fight it by hand". A battle between two crowns on the far side of the map
    // resolves itself and was never this panel's business.
    if (!c || !p || !p.playerFights) {
      if (this.shownKey) this.hide();
      return;
    }
    const key = `${p.province}#${p.attackerNation}v${p.defenderNation}#${p.seed}`;
    if (this.shownKey === key && this.modal.classList.contains('show')) return;
    this.shownKey = key;
    const foe = other(p, c.playerNation);
    this.render(p, foe >= 0 ? nationName(c, foe) : '', isHomeAssault(c));
  }

  private hide(): void {
    this.shownKey = '';
    this.modal.classList.remove('show');
    this.modal.innerHTML = '';
  }

  private render(p: PendingBattle, otherName: string, atHome: boolean): void {
    const attacking = p.playerAttacks;
    const mine = attacking ? p.attackerPoints : p.defenderPoints;
    const theirs = attacking ? p.defenderPoints : p.attackerPoints;
    const title = attacking ? 'The Assault' : atHome ? 'They Are At The Gate' : 'They Have Found You';
    /**
     * The one line that reads differently for the capital, and it is a
     * difference in the *fight* rather than in the screen it is asked on.
     *
     * An assault on your own seat is fought in the city you built, on your own
     * walls, which is a materially different offer from a field battle — and
     * the player is owed that sentence before they answer rather than after.
     */
    const hint = atHome
      ? 'Taking the field fights it in your own streets, with your walls and your towers where you put them. ' +
        'Letting it be decided settles it from the numbers, and a capital settled from the numbers never gets to use its stone.'
      : 'Taking the field fights it properly, on the ground, with your own hands. ' +
        'Letting it be decided settles it from the numbers and costs you the chance to do better than them.';

    this.modal.innerHTML =
      `<div class="wc-sheet">` +
      `<h1>${esc(title)}</h1>` +
      `<div class="wc-where">${esc(provinceName(p.province))} — ` +
      `${attacking ? 'you fall on' : 'you are fallen on by'} ` +
      `${esc(otherName || 'whatever holds it')}.</div>` +
      `<div class="wc-odds">` +
      `<div><span>Yours</span><b>${Math.round(mine)}</b></div>` +
      `<div><span>Theirs</span><b>${Math.round(theirs)}</b></div>` +
      `</div>` +
      `<div class="wc-acts">` +
      `<span class="wc-seal" data-act="fight">Take the field</span>` +
      `<span class="wc-btn" data-act="auto">Let it be decided</span>` +
      `</div>` +
      `<div class="wc-hint">${esc(hint)}</div>` +
      `</div>`;
    this.modal.classList.add('show');

    for (const el of this.modal.querySelectorAll('[data-act]')) {
      const node = el as HTMLElement;
      /**
       * `mousedown` rather than `click`, which is what the rest of this game's
       * chrome answers on and what the screenshot harness dispatches. A `click`
       * wants a press and a release on the same node; on a touch screen a thumb
       * that slides two pixels between them is a press that does nothing, and
       * this is the one panel in the game a player cannot get past by trying
       * again somewhere else.
       */
      node.onmousedown = (ev) => {
        ev.preventDefault();
        if (node.dataset.act === 'fight') this.cb.onFight();
        // The panel is left up: `pending` is the sim's to clear, and the next
        // frame's `update` takes it down once it has been. A refused answer — a
        // race with a save being loaded, say — leaves the question on the table
        // rather than an unfrozen-looking realm that is still frozen.
        else this.cb.onCommand(cmd(Cmd.CampaignAutoResolve));
      };
    }
  }
}

/** Whichever nation in the battle is not mine. */
function other(p: PendingBattle, me: number): number {
  return p.attackerNation === me ? p.defenderNation : p.attackerNation;
}

function esc(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string,
  );
}

let styled = false;
function injectStyle(): void {
  if (styled) return;
  styled = true;
  const s = document.createElement('style');
  s.id = 'warcall-style';
  s.textContent = `
    /*
     * 93: over the world map (50), the resource strip (55), the character sheet
     * (70) and the Ledger (75), and under the realm's chronicle (95) — because a
     * chronicle question and a battle can both be on the table at once, and the
     * chronicle is the one that has to be answered first for the realm to get as
     * far as resolving anything.
     *
     * 93 rather than 92 because the wait lobby took 92 the same night. The two
     * cannot in fact be on screen together — lobby.show runs before
     * session.waitBegin and lobby.hide immediately after it, so there is no
     * campaign yet while it is up — but a tie is decided by document order, and
     * document order is not a thing either file states or defends.
     *
     * (No backticks in this string. It is one template literal, and a backtick
     * in a comment ends the stylesheet — the same trap worldmap.ts records
     * having paid for four times. It caught this comment on the way in.)
     */
    #warcall {
      position: absolute; inset: 0; z-index: 93; display: none;
      align-items: center; justify-content: center;
      background: rgba(8,8,7,0.82); pointer-events: auto;
      font-family: Inter, system-ui, sans-serif; color: var(--bone);
    }
    #warcall.show { display: flex; }
    #warcall .wc-sheet { width: min(520px, 90vw); padding: 30px 32px;
      background: #16140f; border: 1px solid var(--gold-deep);
      box-shadow: 0 30px 90px rgba(0,0,0,0.8); }
    #warcall h1 { font-family: 'Cinzel', Georgia, serif; font-size: 24px;
      letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold); margin-bottom: 10px; }
    #warcall .wc-where { font-style: italic; color: var(--bone-deep); line-height: 1.6;
      font-size: 11.5px; }
    #warcall .wc-odds { display: flex; gap: 28px; margin: 18px 0 22px; }
    #warcall .wc-odds span { display: block; font-size: 10px; letter-spacing: 0.2em;
      text-transform: uppercase; color: var(--bone-deep); }
    #warcall .wc-odds b { font-family: 'Cinzel', Georgia, serif; font-size: 26px; color: var(--bone); }
    #warcall .wc-acts { display: flex; flex-wrap: wrap; gap: 8px; align-items: stretch; }
    #warcall .wc-seal { font-family: 'Cinzel', Georgia, serif; font-size: 12px;
      letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-pale);
      padding: 11px 20px; cursor: pointer; border: 1px solid var(--gold-deep);
      background: rgba(200,164,76,0.1); white-space: nowrap;
      display: flex; align-items: center; }
    #warcall .wc-seal:hover { background: rgba(200,164,76,0.24); }
    #warcall .wc-btn { padding: 11px 16px; cursor: pointer; font-size: 11.5px;
      color: var(--bone-deep); display: flex; align-items: center;
      background: rgba(255,255,255,0.035); border: 1px solid rgba(200,164,76,0.16);
      clip-path: polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%); }
    #warcall .wc-btn:hover { background: rgba(200,164,76,0.16); border-color: var(--gold);
      color: var(--bone); }
    #warcall .wc-hint { color: var(--bone-deep); font-size: 11px; line-height: 1.6; margin-top: 12px; }
    /*
     * In a hand. The sheet is 90vw already; what breaks at 420px is the two
     * buttons side by side — the seal will not wrap, so it shoulders the ghost
     * off the edge — and 30px of padding either side of a 420px screen.
     */
    @media (max-width: 820px) {
      #warcall .wc-sheet { padding: 22px 18px; }
      #warcall h1 { font-size: 20px; }
      #warcall .wc-acts { flex-direction: column; }
      #warcall .wc-seal, #warcall .wc-btn { justify-content: center; }
    }
  `;
  document.head.appendChild(s);
}
